import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, MoreVertical, Info } from 'lucide-react';
import { useUsersEvaluationAdmin } from '../../graphql/hooks/useAdminDashboard';
import { useActiveUsers } from '../../graphql/hooks/useUserDashboard';
import {
  useCloseMonthHours,
  useDeleteClosedMonth,
  useSendReminderPushNotification,
  useReopenMonthPayroll
} from '../../graphql/hooks/useAdminDashboard';
import { Button, ConfirmModal } from '../core';
import { PayrollConfirmModal } from './PayrollconfirmModal';
import { HoursDetailsModal, type HoursDetailsData } from './HoursDetailsModal';
import { SearchableSelect } from '../core/SearchableSelect';
import useDismiss from '../custome_hooks/useDismiss';
import { firstDayOfNextMonth, type RequestsPreset } from './requestFilters';

type MonthClosureViewProps = {
  onOpenRequests?: (preset: RequestsPreset) => void;
};

type MonthClosureSubTab = 'overview' | 'hours';

export function MonthClosureView({ onOpenRequests }: MonthClosureViewProps) {
  const [monthClosureSubTab, setMonthClosureSubTab] =
    useState<MonthClosureSubTab>('overview');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const closeMonthPicker = useCallback(() => setShowMonthPicker(false), []);
  useDismiss(monthPickerRef, showMonthPicker, closeMonthPicker);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [openDropdownDate, setOpenDropdownDate] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<HoursDetailsData | null>(null);

  const { mutate: deleteClosedMonthMutate } = useDeleteClosedMonth();
  const { mutate: sendReminderMutate } = useSendReminderPushNotification();
  const { mutate: reopenMonthPayrollMutate } = useReopenMonthPayroll();

  const getCurrentMonthString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthString());
  const [year, month] = selectedMonth.split('-').map(Number);
  const [userId, setUserId] = useState<string>('');

  const { data: userEvaluation } = useUsersEvaluationAdmin({
    userId,
    month,
    year,
  });
  const { data: closeMonthHours } = useCloseMonthHours({ userId, month, year });

  const hoursData = closeMonthHours ?? [];
  const userMonthlyClosing = userEvaluation?.getEvaluation;
  const timeCredit = userMonthlyClosing?.time_credit_overview;
  const hasFutureCredit = timeCredit?.time_credit_incl_future != null;
  const { data: users } = useActiveUsers();
  const userOptions = useMemo(
    () => (users ?? []).map((user) => ({ value: user.id, label: user.full_name })),
    [users]
  );

  const monthNames = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  const formatMonthLabel = (value: string) => {
    const [year, month] = value.split('-');
    return `${monthNames[Number(month) - 1]} ${year}`;
  };

  const formatMonthForState = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const renderMonthPicker = () => {
    const months = [
      'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
    ];

    return (
      <>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear() - 1, 0, 1))
            }
          >
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>

          <div className="text-sm font-medium">{currentMonth.getFullYear()}</div>

          <button
            onClick={() =>
              setCurrentMonth(new Date(currentMonth.getFullYear() + 1, 0, 1))
            }
          >
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
        </div>

        {/* Months grid */}
        <div className="grid grid-cols-3 gap-2">
          {months.map((month, index) => {
            const isSelected =
              selectedMonth ===
              formatMonthForState(new Date(currentMonth.getFullYear(), index, 1));

            return (
              <button
                key={month}
                onClick={() => {
                  const newDate = new Date(currentMonth.getFullYear(), index, 1);
                  setSelectedMonth(formatMonthForState(newDate));
                  setCurrentMonth(newDate);
                  setShowMonthPicker(false);
                }}
                className={`h-10 rounded-md text-sm transition-colors ${
                  isSelected ? 'bg-[#0084c7] text-white' : 'hover:bg-[#f5f5f5]'
                }`}
              >
                {month}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  useEffect(() => {
    if (!userId && users?.length) {
      setUserId(users[0].id);
    }
  }, [users, userId]);

  const monthNotClosedByUser = !userMonthlyClosing?.month_closed_user;

  const handleSendReminder = () => {
    if (!userId) return;
    sendReminderMutate({ user_id: userId, month, year });
  };

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Month Selector */}
        <div className="py-3">
          <div ref={monthPickerRef} className="relative">
            <button
              onClick={() => setShowMonthPicker((prev) => !prev)}
              className="px-4 py-2 border border-[#e9e9e9] rounded-lg text-[14px] text-[#1c1c1c] bg-white hover:border-[#0084c7]"
            >
              {formatMonthLabel(selectedMonth)}
            </button>

            {showMonthPicker && (
              <div className="absolute mt-2 w-[260px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4 z-30">
                {renderMonthPicker()}
              </div>
            )}
          </div>
        </div>

        {/* Subtab Toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonthClosureSubTab('overview')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${
              monthClosureSubTab === 'overview'
                ? 'text-[#0084c7] font-medium'
                : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
            }`}
          >
            Übersicht
            {monthClosureSubTab === 'overview' && (
              <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />
            )}
          </button>

          <button
            onClick={() => setMonthClosureSubTab('hours')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${
              monthClosureSubTab === 'hours'
                ? 'text-[#0084c7] font-medium'
                : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
            }`}
          >
            Stunden
            {monthClosureSubTab === 'hours' && (
              <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />
            )}
          </button>
        </div>
      </div>

      {/* Data Display */}
      {monthClosureSubTab === 'overview' && (
        <div className="space-y-6">
          {/* Employee Selector */}
          <div className="flex items-center gap-2">
            <SearchableSelect
              value={userId || undefined}
              onChange={(id) => id && setUserId(id)}
              options={userOptions}
              placeholder="Mitarbeiter auswählen"
              searchPlaceholder="Mitarbeiter suchen..."
              noResultsText="Keine Mitarbeiter gefunden"
              className="min-w-[220px]"
            />

            {(userMonthlyClosing?.month_closed_user ||
              userMonthlyClosing?.month_closed_admin) && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-[10px] px-5 py-3">
                <div className="flex flex-col">
                  {userMonthlyClosing?.month_closed_user && (
                    <span className="text-[16px] font-medium text-green-700">
                      Monat abgeschlossen
                    </span>
                  )}
                  {userMonthlyClosing?.month_closed_admin && (
                    <span className="text-[14px] text-green-700">
                      Für Lohnbüro vorbereitet
                    </span>
                  )}
                </div>

                {userMonthlyClosing?.month_closed_user && (
                  <Button
                    variant="secondary"
                    onClick={() => setReopenConfirmOpen(true)}
                  >
                    Monat erneut öffnen
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Top 3 Columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Urlaubskonto */}
            <div className="space-y-4 bg-white rounded-[10px] border border-[#e9e9e9] p-4">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                Urlaubskonto
              </h3>
              <div className="space-y-3">
                <Row
                  label="Urlaub aktueller Monat"
                  value={`${userMonthlyClosing?.holiday_overview?.holiday_used_month ?? 0} Tage`}
                />
                <Row
                  label="Jahresurlaub"
                  value={`${userMonthlyClosing?.holiday_overview?.holiday_total ?? 0} Tage`}
                />
                <Row
                  label="Resturlaub"
                  value={`${userMonthlyClosing?.holiday_overview?.holiday_open ?? 0} Tage | ${userMonthlyClosing?.holiday_overview?.holiday_pending ?? 0} Tage`}
                />
              </div>
            </div>

            {/* Zeitkonto */}
            <div className="space-y-4 bg-white rounded-[10px] border border-[#e9e9e9] p-4">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                Zeitkonto
              </h3>
              <div className="space-y-3">
                {hasFutureCredit ? (
                  <>
                    <Row
                      label="Zeitguthaben"
                      value={`${(timeCredit?.time_credit_incl_future ?? 0).toFixed(2)} Std`}
                      tooltip={timeCredit?.info_time_credit_incl_future ?? undefined}
                    />
                    <Row
                      label="Zukünftiger Überstundenabbau"
                      value={`${(timeCredit?.future_overtime_reduction ?? 0).toFixed(2)} Std`}
                      tooltip={timeCredit?.info_future_overtime_reduction ?? undefined}
                      onClick={
                        onOpenRequests && userId
                          ? () => onOpenRequests({ userId, type: 'overtime', from: firstDayOfNextMonth(month, year) })
                          : undefined
                      }
                    />
                  </>
                ) : (
                  // month closed before the "incl. future" values existed: show the old value, no future row
                  <Row
                    label="Zeitguthaben"
                    value={`${(timeCredit?.time_credit ?? 0).toFixed(2)} Std`}
                    tooltip={timeCredit?.info_time_credit}
                  />
                )}
                <Row
                  label="Überstunden abgefeiert"
                  value={`${(userMonthlyClosing?.time_credit_overview?.overtime_hours ?? 0).toFixed(2)} Std`}
                />
                <Row
                  label="Nach Abschluss ausbezahlt"
                  value={`${(userMonthlyClosing?.time_credit_overview?.overtime_paid ?? 0).toFixed(2)} Std`}
                  labelColor="bg-green-50 text-green-600 border-green-200"
                />
                <Row
                  label="Überstundenabzug"
                  value={`${(userMonthlyClosing?.time_credit_overview?.overtime_deduction ?? 0).toFixed(2)} Std`}
                  labelColor="bg-red-50 text-red-600 border-red-200"
                />
                <Row
                  label="Fester Abzug"
                  value={`${(userMonthlyClosing?.time_credit_overview?.fixed_time ?? 0).toFixed(2)} Std`}
                />
                <Row
                  label="Zeitguthaben nach Abzug"
                  value={`${(userMonthlyClosing?.time_credit_overview?.remaining_time_credit ?? 0).toFixed(2)} Std`}
                  tooltip={userMonthlyClosing?.time_credit_overview?.info_remaining_time_credit}
                />
              </div>
            </div>

            {/* Wochenschichten */}
            <div className="space-y-4 bg-white rounded-[10px] border border-[#e9e9e9] p-4">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                Wochenschichten
              </h3>
              <div className="space-y-3">
                <Row
                  label="Notdienst"
                  value={`${userMonthlyClosing?.weekend_work_overview?.weekend_emergency_service ?? 0} Tage`}
                />
                <Row
                  label="Baustelle"
                  value={`${
                    (userMonthlyClosing?.weekend_work_overview?.weekend_time_trackings ?? 0) -
                    (userMonthlyClosing?.weekend_work_overview?.weekend_emergency_service ?? 0)
                  } Tage`}
                />
                <Row
                  label="Gesamt"
                  value={`${userMonthlyClosing?.weekend_work_overview?.weekend_time_trackings ?? 0} Tage`}
                />
              </div>
            </div>
          </div>

          {/* Bottom 2 Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* Gesonderte Auszahlungen */}
            <div className="space-y-4 bg-white rounded-[10px] border border-[#e9e9e9] p-4">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                Gesonderte Auszahlungen
              </h3>
              <div className="space-y-3">
                <Row
                  label="Nachtarbeit"
                  value={`${(userMonthlyClosing?.separate_payment_overview?.night_hours ?? 0).toFixed(2)} Std`}
                />
                <Row
                  label="Notdienst"
                  value={`${(userMonthlyClosing?.separate_payment_overview?.emergency_service_payment ?? 0).toFixed(2)} €`}
                  tooltip={userMonthlyClosing?.separate_payment_overview?.info_emergency_service_payment}
                />
                <Row
                  label="Spesen"
                  value={`${(userMonthlyClosing?.separate_payment_overview?.expenses_payment ?? 0).toFixed(2)} €`}
                  tooltip={userMonthlyClosing?.separate_payment_overview?.info_expenses_payment}
                />
              </div>
            </div>

            {/* Stundenkonto */}
            <div className="space-y-4 bg-white rounded-[10px] border border-[#e9e9e9] p-4">
              <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-6 pb-3 border-b border-[#e9e9e9]">
                Stundenkonto
              </h3>
              <div className="space-y-3">
                <Row
                  label="Ist Stunden"
                  value={`${(
                    (userMonthlyClosing?.hour_overview?.regular_hours ?? 0) +
                    (userMonthlyClosing?.hour_overview?.overtime_hours ?? 0) +
                    (userMonthlyClosing?.hour_overview?.sunday_hours ?? 0) +
                    (userMonthlyClosing?.hour_overview?.night_hours ?? 0)
                  ).toFixed(2)} Std`}
                />
                <div className="flex justify-between items-start">
                  <span className="text-[#6b6b6b] text-[14px]" />
                  <span className="text-[#6b6b6b] text-[12px] text-right">
                    {`100% ${(userMonthlyClosing?.hour_overview?.regular_hours ?? 0).toFixed(2)} Std | 
                    125% ${(userMonthlyClosing?.hour_overview?.overtime_hours ?? 0).toFixed(2)} Std | 
                    Nacht ${(userMonthlyClosing?.hour_overview?.night_hours ?? 0).toFixed(2)} Std | 
                    Sonntag ${(userMonthlyClosing?.hour_overview?.sunday_hours ?? 0).toFixed(2)} Std`}
                  </span>
                </div>
                <Row
                  label="Ist Std inkl. Zuschlag"
                  value={`${(userMonthlyClosing?.hour_overview?.ist_sum_inkl_zuschlag ?? 0).toFixed(2)} Std`}
                />
                <Row
                  label="Soll Stunden"
                  value={`${(userMonthlyClosing?.hour_overview?.month_working_hours ?? 0).toFixed(2)} Std`}
                />
                <Row
                  label="Arbeitstage/Monat"
                  value={`${userMonthlyClosing?.hour_overview?.month_working_days ?? 0} Tage`}
                />
                <Row
                  label="Geleistete Tage/Monat"
                  value={`${userMonthlyClosing?.hour_overview?.tracked_working_days ?? 0} Tage`}
                />
                <Row
                  label="Krankentage"
                  value={`${userMonthlyClosing?.hour_overview?.sickdays ?? 0} Tage`}
                />
                <Row
                  label="Wochenende"
                  value={`${userMonthlyClosing?.hour_overview?.weekend_work_days ?? 0} Tage`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {monthClosureSubTab === 'hours' && (
        <div className="space-y-6">
          {/* User Selector */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-start">
            <div className="flex items-center gap-2">
              <SearchableSelect
                value={userId || undefined}
                onChange={(id) => id && setUserId(id)}
                options={userOptions}
                placeholder="Mitarbeiter auswählen"
                searchPlaceholder="Mitarbeiter suchen..."
                noResultsText="Keine Mitarbeiter gefunden"
                className="min-w-[220px]"
              />
            </div>
          </div>

          {/* Hours Table */}
          <div className="space-y-4">
            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_0.7fr_0.8fr_44px] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
              <div>Datum</div>
              <div>Gesamt Stunden</div>
              <div>Notdienst</div>
              <div>Nachtarbeit</div>
            </div>

            {/* Rows */}
            {hoursData.map((entry) => (
              <div
                key={entry.date}
                className="grid grid-cols-[1fr_1fr_0.7fr_0.8fr_44px] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors"
              >
                <div
                  className={
                    entry.showAsHighlighted
                      ? 'text-red-600 font-bold'
                      : 'text-[#1c1c1c]'
                  }
                >
                  {entry.date}
                </div>
                <div className="text-[#1c1c1c]">{entry.total_hours}</div>
                <div className="text-[#1c1c1c]">{entry.emergency ? 'ja' : 'nein'}</div>
                <div className="text-[#1c1c1c]">{entry.nightWork ? 'ja' : 'nein'}</div>

                {/* Actions */}
                <div className="relative flex justify-end">
                  <button
                    onClick={() =>
                      setOpenDropdownDate(
                        openDropdownDate === entry.date ? null : entry.date
                      )
                    }
                    className="p-1 rounded-[6px] transition-colors"
                    aria-label="Mehr Optionen"
                  >
                    <MoreVertical className="size-5 text-[#6b6b6b]" />
                  </button>

                  {openDropdownDate === entry.date && (
                    <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[140px] overflow-hidden">
                      <button
                        onClick={() => {
                          setOpenDropdownDate(null);
                          setSelectedDetails(entry);
                          setDetailsOpen(true);
                        }}
                        className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
                      >
                        Details
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        {monthNotClosedByUser && userId && (
          <Button variant="secondary" onClick={handleSendReminder}>
            Reminder senden
          </Button>
        )}
        {!userMonthlyClosing?.month_closed_admin && (
          <Button
            variant="primary"
            onClick={() => setConfirmOpen(true)}
            className="flex items-center gap-2"
          >
            Abrechnung vorbereiten
          </Button>
        )}
      </div>

      {confirmOpen && (
        <PayrollConfirmModal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          data={{
            name: userMonthlyClosing?.user.full_name ?? '',
            userId: userId,
            year,
            monthToClose: month,
            month: selectedMonth,
            jobTitle: userMonthlyClosing?.user.job_title ?? '',
            status: userMonthlyClosing?.user.status ?? '',
            internalNumber: userMonthlyClosing?.user.internal_number ?? '',
            entryDate: userMonthlyClosing?.user.entry_date ?? '',
            monthHours: userMonthlyClosing?.hour_overview?.month_working_hours ?? 0,
            fixedDeduction: userMonthlyClosing?.time_credit_overview?.fixed_time ?? 0,
            nightWork: userMonthlyClosing?.separate_payment_overview?.night_hours ?? 0,
            emergencyService:
              userMonthlyClosing?.separate_payment_overview?.emergency_service_payment ?? 0,
            expenses: userMonthlyClosing?.separate_payment_overview?.expenses_payment ?? 0,
            sundayWork: userMonthlyClosing?.hour_overview?.sunday_hours ?? 0,
            timeBalance: userMonthlyClosing?.time_credit_overview?.time_credit ?? 0,
            closingComment: userMonthlyClosing?.comment ?? undefined,
          }}
        />
      )}

      {reopenConfirmOpen && (
        <ConfirmModal
          isOpen={reopenConfirmOpen}
          onClose={() => setReopenConfirmOpen(false)}
          message="Möchten Sie diesen Monat wirklich erneut öffnen?"
          title="Monat erneut öffnen"
          onConfirm={() => {
            const isFullyClosed =
              userMonthlyClosing?.month_closed_user &&
              userMonthlyClosing?.month_closed_admin;

            const onSuccess = () => setReopenConfirmOpen(false);

            if (isFullyClosed) {
              reopenMonthPayrollMutate(
                { userId, month, year },
                { onSuccess }
              );
            } else {
              deleteClosedMonthMutate(
                { user_id: userId, month, year },
                { onSuccess }
              );
            }
          }}
        />
      )}

      <HoursDetailsModal
        isOpen={detailsOpen}
        data={selectedDetails}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedDetails(null);
        }}
      />
    </div>
  );
}

function Row({
  label,
  value,
  labelColor,
  tooltip,
  onClick,
}: {
  label: string;
  value: string;
  labelColor?: string;
  tooltip?: string;
  onClick?: () => void;
}) {
  const labelContent = (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {tooltip && (
        <span className="relative group inline-flex">
          <Info className="size-3.5 text-current opacity-70 cursor-help" />
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-[260px] z-20">
            <span className="block bg-white text-[#1c1c1c] text-[12px] px-3 py-2 rounded-[8px] shadow-lg border border-[#e9e9e9] whitespace-pre-line text-left font-normal">
              {tooltip}
            </span>
          </span>
        </span>
      )}
    </span>
  );

  if (labelColor) {
    return (
      <div className="flex justify-between items-center">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[14px] font-medium border ${labelColor}`}
        >
          {labelContent}
        </span>
        <span className="text-[16px] font-medium text-[#1c1c1c]">{value}</span>
      </div>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group/row w-[calc(100%+1rem)] flex justify-between items-center -mx-2 px-2 py-1 rounded-md text-left hover:bg-[#f5f5f5] transition-colors"
      >
        <span className="text-[#0084c7] text-[14px] group-hover/row:underline">{labelContent}</span>
        <span className="inline-flex items-center gap-1 shrink-0 whitespace-nowrap text-[16px] font-medium text-[#1c1c1c]">
          {value}
          <ChevronRight className="size-4 text-[#6b6b6b]" />
        </span>
      </button>
    );
  }

  return (
    <div className="flex justify-between items-center">
      <span className="text-[#6b6b6b] text-[14px]">{labelContent}</span>
      <span className="text-[16px] font-medium text-[#1c1c1c]">{value}</span>
    </div>
  );
}